'use client';

import { useState, useCallback } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kit/ui/dialog';
import { Button } from '@kit/ui/button';
import { Separator } from '@kit/ui/separator';
import { Input } from '@kit/ui/input';
import { toast } from "sonner";
import Link from 'next/link';
import { Trans } from '@kit/ui/trans';
import pathsConfig from '~/config/paths.config';
import appConfig from '~/config/app.config';
import { removeChatSessionAction } from '~/lib/server/messages/removeChatSessionAction';
import { ButtonModeToggle } from '@kit/ui/button-mode-toggle';


interface ChatOptionsDialogProps {
  url: string;
  showBrand?: boolean;
  showThemeSwitcher?: boolean;
}

export function ChatOptionsDialog({
  url,
  showBrand = true,
  showThemeSwitcher = true,
  children,
}: React.PropsWithChildren<ChatOptionsDialogProps>) {

  const [isOpen, setIsOpen] = useState(false);

  const copyToClipboard = useCallback(async () => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  }, [url]);

  const restartChat = async () => {
    const slug = url.split('/').pop() || '';
    await removeChatSessionAction({ slug: slug });
    window.location.reload();
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} modal>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {showBrand ? (
              <Trans i18nKey={'teams:shareMagicLinkTitle'} />
            ) : (
              <Trans i18nKey={'common:chatPageSettingsTitle'} />
            )}
          </DialogTitle>
        </DialogHeader>
        <div>
          {showBrand && (
            <>
              <div className="flex space-x-2">
                <Input value={url} readOnly />
                <Button 
                  variant="secondary" 
                  className="shrink-0" 
                  onClick={copyToClipboard}
                >
                  <Trans i18nKey={'teams:copyLinkLabel'} />
                </Button>
              </div>
              <Separator className='my-4'/>
              <div className='flex flex-col space-y-1.5 text-center sm:text-left'>
                <div className='text-lg font-semibold leading-none tracking-tight'>
                  <Trans
                    i18nKey={'teams:joinWithLabel'}
                    values={{
                      brandName: appConfig.name,
                    }}
                  />
                </div>
                <div className='text-sm text-muted-foreground'>
                  <Trans
                    i18nKey={'teams:joinDescriptionLabel'}
                    values={{
                      brandName: appConfig.name,
                    }}
                  />
                </div>
                <div className='flex flex-row space-x-1.5'>
                  <Button className='flex-1' variant="default" asChild>
                    <Link href={pathsConfig.auth.signUp}>
                      <Trans i18nKey={'auth:signUp'} />
                    </Link>
                  </Button>
                  <Button className='flex-1' variant="default" asChild>
                    <Link href={pathsConfig.auth.signUp}>
                      <Trans i18nKey={'common:learnMore'} />
                    </Link>
                  </Button>
                </div>
              </div>
              <Separator className='my-4'/>
            </>
          )}
          <div className='flex flex-col space-y-1.5 text-center sm:text-left'>
            {showBrand && (
              <>
                <div className='text-lg font-semibold leading-none tracking-tight'>
                  <Trans i18nKey={'common:chatPageSettingsTitle'} />
                </div>
                <div className='text-sm text-muted-foreground'>
                  <Trans i18nKey={'common:chatPageSettingsDescription'} />
                </div>
              </>
            )}
            <div className='flex flex-col space-y-1.5'>
              <Button className='flex-1' variant="secondary" onClick={restartChat}>
                <Trans i18nKey={'common:chatPageSettingsRestartButton'} />
              </Button>
              {showThemeSwitcher && <ButtonModeToggle />}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}